import React, { useState, useEffect } from 'react';
import {
    Grid,
    Typography,
    TextField,
    Button,
    Dialog,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    FormHelperText,
    FormControlLabel,
    Checkbox,
    Box,
} from '@mui/material';
import { Formik, Form, Field } from 'formik';
import { toast } from 'react-toastify';
import AppDialogTitle from '../../../ui/appDialogTitle';
import { isLocalDev } from '../../../../utils/environment';
import { LensConfigurationService, ValidDataService } from '../../../../service/validDataService';
import validLookup from '../../../../utils/validLookup';
import SafeDialogContent from 'components/ui/SafeDialogContent';

// Helper function to format lens type for display
const formatLensType = (lensType) => {
    if (!lensType) return '';
    return lensType.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
};

const toNumber = (value) => {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const calculateRetailFromMargin = (baseCost, targetMarginPct) => {
    const cost = toNumber(baseCost);
    const margin = toNumber(targetMarginPct);
    if (cost === null || margin === null || margin >= 100) return null;
    const retail = cost / (1 - margin / 100);
    return Number.isFinite(retail) ? parseFloat(retail.toFixed(2)) : null;
};

const calculateMarginFromRetail = (baseCost, baseRetailPrice) => {
    const cost = toNumber(baseCost);
    const retail = toNumber(baseRetailPrice);
    if (cost === null || retail === null || retail <= 0) return null;
    const margin = ((retail - cost) / retail) * 100;
    return Number.isFinite(margin) ? parseFloat(margin.toFixed(2)) : null;
};

export const initialValuesLensPricingMatrixForm = {
    lensModelId: '',
    lensMaterialId: '',
    lensIndexId: '',
    // sphereMin: '',   // Rx-based tiered pricing — commented out until pricing lookup is implemented
    // sphereMax: '',
    // cylinderMin: '',
    // cylinderMax: '',
    baseCost: 0,
    targetMarginPct: '',
    baseRetailPrice: 0,
    status: validLookup.validStatus.active,
    notes: '',
    treatmentIds: [], // Array of treatment IDs (just availability, not pricing)
};

export default function LensPricingMatrixInputForm({ open, onClose, onRecordAdded }) {
    const [manufacturers, setManufacturers] = useState([]);
    const [lensModels, setLensModels] = useState([]);
    const [materials, setMaterials] = useState([]);
    const [indexes, setIndexes] = useState([]);
    const [availableTreatments, setAvailableTreatments] = useState([]);

    useEffect(() => {
        if (open) {
            // Fetch manufacturers
            ValidDataService.getManufacturersByType('lens').then(result => {
                const { code, data } = result;
                if (code === 0) {
                    setManufacturers(data || []);
                }
            }).catch(error => {
                if (isLocalDev) console.error(error);
            });

            // Fetch materials
            LensConfigurationService.getMaterialsWithLimit(0, 1000, [], []).then(result => {
                if (result?.code === 0) {
                    setMaterials(result.data?.rows?.filter(m => m.status === 'active') || []);
                }
            }).catch(console.error);

            // Fetch indexes
            LensConfigurationService.getIndexesWithLimit(0, 1000, [], []).then(result => {
                if (result?.code === 0) {
                    setIndexes(result.data?.rows?.filter(i => i.status === 'active') || []);
                }
            }).catch(console.error);

            // Fetch treatments
            LensConfigurationService.getTreatmentsWithLimit(0, 1000, [], []).then(result => {
                if (result?.code === 0) {
                    setAvailableTreatments(result.data?.rows?.filter(t => t.status === 'active') || []);
                }
            }).catch(console.error);
        }
    }, [open]);

    const handleManufacturerChange = async (manufacturerId, setFieldValue) => {
        setFieldValue('manufacturerId', manufacturerId);
        setFieldValue('lensModelId', '');
        setLensModels([]);

        if (manufacturerId) {
            try {
                const result = await LensConfigurationService.getModelsByManufacturer(manufacturerId);
                const { code, data } = result;
                if (code === 0) {
                    setLensModels(data?.filter(m => m.status === 'active') || []);
                }
            } catch (error) {
                if (isLocalDev) console.error(error);
            }
        }
    };

    const handleSubmit = async (values, { resetForm, setSubmitting }) => {
        try {
            // Convert treatmentIds array to treatments array for backend
            const dataToSubmit = {
                ...values,
                treatments: values.treatmentIds.map(id => ({ lensTreatmentId: id }))
            };
            delete dataToSubmit.treatmentIds;

            const response = await LensConfigurationService.addPricingMatrix(dataToSubmit);
            const { code, data, message } = response;

            setSubmitting(false);

            if (code === 0) {
                toast.success(message);
                resetForm();
                onRecordAdded(data);
                onClose();
            } else {
                toast.error(message);
            }
        } catch (error) {
            if (isLocalDev) {
                console.error(error);
            }
            setSubmitting(false);
            toast.error('Failed to add pricing matrix entry');
        }
    };

    return (
        <Dialog open={open} maxWidth={'md'} fullWidth>
            <AppDialogTitle title={"Add Lens Pricing Rule"} onClose={onClose} />
            <SafeDialogContent sx={{ mt: 1, mb: 1 }}>
                <Formik
                    initialValues={initialValuesLensPricingMatrixForm}
                    onSubmit={handleSubmit}
                >
                    {({ isSubmitting, values, setFieldValue, handleChange, handleBlur, touched, errors }) => (
                        <Form>
                            <Grid container spacing={2} sx={{mt: 1}}>
                                {/* Manufacturer Selection - Used to filter models */}
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Filter by Manufacturer</InputLabel>
                                        <Select
                                            name="manufacturerId"
                                            value={values.manufacturerId || ''}
                                            onChange={(e) => handleManufacturerChange(e.target.value, setFieldValue)}
                                            onBlur={handleBlur}
                                            label="Filter by Manufacturer"
                                        >
                                            {manufacturers.map((mfr) => (
                                                <MenuItem key={mfr.id} value={mfr.id}>
                                                    {mfr.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <FormHelperText>Select to filter lens models</FormHelperText>
                                    </FormControl>
                                </Grid>

                                {/* Lens Model */}
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth size="small" required disabled={!values.manufacturerId}>
                                        <InputLabel>Lens Model</InputLabel>
                                        <Select
                                            name="lensModelId"
                                            value={values.lensModelId || ''}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            label="Lens Model"
                                        >
                                            {lensModels.map((model) => (
                                                <MenuItem key={model.id} value={model.id}>
                                                    {formatLensType(model.lensType)} - {model.modelName} 
                                                    {model.manufacturerBrand?.brandName ? ` (${model.manufacturerBrand.brandName})` : ''}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* Material */}
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth size="small" required>
                                        <InputLabel>Material</InputLabel>
                                        <Select
                                            name="lensMaterialId"
                                            value={values.lensMaterialId || ''}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            label="Material"
                                        >
                                            {materials.map((material) => (
                                                <MenuItem key={material.id} value={material.id}>
                                                    {material.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* Index */}
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth size="small" required>
                                        <InputLabel>Index</InputLabel>
                                        <Select
                                            name="lensIndexId"
                                            value={values.lensIndexId || ''}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            label="Index"
                                        >
                                            {indexes.map((index) => (
                                                <MenuItem key={index.id} value={index.id}>
                                                    {index.indexValue}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* Prescription Ranges — commented out until Rx-based tiered pricing lookup is implemented
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                                        Prescription Range (Optional - leave blank for all)
                                    </Typography>
                                </Grid>

                                <Grid item xs={6} sm={3}>
                                    <Field
                                        as={TextField}
                                        name="sphereMin"
                                        label="Sphere Min"
                                        type="number"
                                        fullWidth
                                        size="small"
                                        inputProps={{ step: "0.25" }}
                                    />
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Field
                                        as={TextField}
                                        name="sphereMax"
                                        label="Sphere Max"
                                        type="number"
                                        fullWidth
                                        size="small"
                                        inputProps={{ step: "0.25" }}
                                    />
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Field
                                        as={TextField}
                                        name="cylinderMin"
                                        label="Cylinder Min"
                                        type="number"
                                        fullWidth
                                        size="small"
                                        inputProps={{ step: "0.25" }}
                                    />
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <Field
                                        as={TextField}
                                        name="cylinderMax"
                                        label="Cylinder Max"
                                        type="number"
                                        fullWidth
                                        size="small"
                                        inputProps={{ step: "0.25" }}
                                    />
                                </Grid>
                                */}

                                {/* Base Pricing */}
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                                        Base Pricing
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        name="baseCost"
                                        label="Base Cost"
                                        type="number"
                                        required
                                        fullWidth
                                        size="small"
                                        value={values.baseCost}
                                        onChange={(e) => {
                                            const nextCost = e.target.value;
                                            setFieldValue('baseCost', nextCost);
                                            const calculatedRetail = calculateRetailFromMargin(nextCost, values.targetMarginPct);
                                            if (calculatedRetail !== null) {
                                                setFieldValue('baseRetailPrice', calculatedRetail);
                                            }
                                        }}
                                        inputProps={{ step: "0.01" }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        name="targetMarginPct"
                                        label="Target Margin %"
                                        type="number"
                                        fullWidth
                                        size="small"
                                        value={values.targetMarginPct}
                                        onChange={(e) => {
                                            const nextMargin = e.target.value;
                                            setFieldValue('targetMarginPct', nextMargin);
                                            const calculatedRetail = calculateRetailFromMargin(values.baseCost, nextMargin);
                                            if (calculatedRetail !== null) {
                                                setFieldValue('baseRetailPrice', calculatedRetail);
                                            }
                                        }}
                                        inputProps={{ step: "0.01", min: "0", max: "99.99" }}
                                        helperText="Retail auto-calculates from cost + margin"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        name="baseRetailPrice"
                                        label="Base Retail Price"
                                        type="number"
                                        required
                                        fullWidth
                                        size="small"
                                        value={values.baseRetailPrice}
                                        onChange={(e) => {
                                            const nextRetail = e.target.value;
                                            setFieldValue('baseRetailPrice', nextRetail);
                                            const calculatedMargin = calculateMarginFromRetail(values.baseCost, nextRetail);
                                            if (calculatedMargin !== null) {
                                                setFieldValue('targetMarginPct', calculatedMargin);
                                            }
                                        }}
                                        inputProps={{ step: "0.01" }}
                                    />
                                </Grid>

                                {/* Available Treatments */}
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                                        Available Treatments (Optional)
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                                        Select which treatments can be applied to this lens configuration. Treatment pricing is managed in the Treatments section.
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                        {availableTreatments.map((treatment) => (
                                            <FormControlLabel
                                                key={treatment.id}
                                                control={
                                                    <Checkbox
                                                        checked={values.treatmentIds.includes(treatment.id)}
                                                        onChange={(e) => {
                                                            const newTreatmentIds = e.target.checked
                                                                ? [...values.treatmentIds, treatment.id]
                                                                : values.treatmentIds.filter(id => id !== treatment.id);
                                                            setFieldValue('treatmentIds', newTreatmentIds);
                                                        }}
                                                    />
                                                }
                                                label={
                                                    <Box>
                                                        <Typography variant="body2">
                                                            {treatment.name}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            Cost: ${parseFloat(treatment.cost || 0).toFixed(2)} | 
                                                            Retail: ${parseFloat(treatment.retailPrice || 0).toFixed(2)}
                                                        </Typography>
                                                    </Box>
                                                }
                                            />
                                        ))}
                                        {availableTreatments.length === 0 && (
                                            <Typography variant="body2" color="text.secondary">
                                                No treatments available. Add treatments in the Treatments section first.
                                            </Typography>
                                        )}
                                    </Box>
                                </Grid>

                                {/* Status */}
                                <Grid item xs={12} sm={6}>
                                    <Field
                                        as={TextField}
                                        name='status'
                                        label="Status"
                                        fullWidth
                                        select
                                        size="small"
                                    >
                                        <MenuItem value={"active"}>Active</MenuItem>
                                        <MenuItem value={"inactive"}>Inactive</MenuItem>
                                    </Field>
                                </Grid>

                                {/* Notes */}
                                <Grid item xs={12}>
                                    <Field
                                        as={TextField}
                                        name="notes"
                                        label="Notes"
                                        multiline
                                        rows={2}
                                        fullWidth
                                        size="small"
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        disabled={isSubmitting}
                                    >
                                        Create
                                    </Button>
                                </Grid>
                            </Grid>
                        </Form>
                    )}
                </Formik>
            </SafeDialogContent>
        </Dialog>
    );
};
